import {useCallback, useEffect, useState} from "react";

export const THEME_STORAGE_KEY = "vt-theme";

/**
 * Resolve the initial theme:
 *   1. an explicit stored preference ("light" | "dark") wins,
 *   2. otherwise fall back to the OS setting (prefers-color-scheme),
 *   3. otherwise default to light.
 */
export function getInitialTheme() {
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (stored === "light" || stored === "dark") {
            return stored;
        }
    } catch (e) {
        // localStorage can throw in private mode / sandboxed iframes — ignore.
    }
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }
    return "light";
}

/** Add/remove the `dark` class on <html> so Tailwind's class-based dark mode applies. */
export function applyTheme(theme) {
    const root = document.documentElement;
    if (theme === "dark") {
        root.classList.add("dark");
    } else {
        root.classList.remove("dark");
    }
}

/**
 * Theme hook: tracks "light" | "dark", persists an explicit choice to localStorage,
 * and keeps the <html> `dark` class in sync. Default follows the OS until the user
 * makes an explicit choice.
 */
export function useTheme() {
    const [theme, setThemeState] = useState(getInitialTheme);

    useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    const setTheme = useCallback((next) => {
        setThemeState(next);
        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, next);
        } catch (e) {
            // ignore persistence failures
        }
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme(theme === "dark" ? "light" : "dark");
    }, [theme, setTheme]);

    return {theme, setTheme, toggleTheme, isDark: theme === "dark"};
}
